import type { PublicPlayer, VoteChoice } from 'shared';
import { Avatar } from '../design/Avatar';
import { Panel } from '../design/Panel';
import { Label } from '../design/Paper';
import { VoteCard, type CardFace } from '../design/VoteCard';

export interface Ballot {
  player: PublicPlayer;
  face: CardFace;
}

/** Tens of milliseconds apart: one wave, still simultaneous to the eye. */
const RIPPLE_MS = 45;

/**
 * The row of ballot cards with each voter beneath. This is the register the
 * whole app exists for, so it never shrinks and it gets the largest card size.
 *
 * Rendered from one keyed position across both the voting and revealed phases,
 * so the cards are the *same* component instances and turn from their actual
 * previous state instead of remounting already-flipped.
 */
export function BallotRow({
  ballots,
  caption,
  animate = true,
}: {
  ballots: Ballot[];
  caption?: string;
  /** False for a device that arrived after the reveal. */
  animate?: boolean;
}) {
  return (
    // Hugs its cards rather than spanning the frame, so a five-player row does
    // not read as mostly empty paper; still capped so fifteen players fit.
    <Panel
      tone="sunk"
      press="md"
      className="mx-auto w-fit max-w-full shrink-0"
      innerClassName="px-scr-gut py-scr-2"
    >
      <div className="flex items-end justify-center gap-[2.4cqw]">
        {ballots.map(({ player, face }, index) => (
          <div
            key={player.id}
            className="flex min-w-0 flex-col items-center gap-scr-1"
            data-testid="ballot"
            data-name={player.name}
            data-face={face}
          >
            <VoteCard
              face={face}
              size="lg"
              animate={animate}
              delay={(index * RIPPLE_MS) / 1000}
            />
            <Avatar
              subject={player}
              size="sm"
              dimmed={!player.alive}
              className="mt-scr-1 h-[8cqh] w-[8cqh] min-h-5 min-w-5 text-[2.6cqh]"
            />
            <span className="max-w-[12cqw] truncate text-center text-screen-sm leading-tight">
              {player.name}
            </span>
          </div>
        ))}
      </div>
      {caption && (
        <div className="mt-scr-2 text-center">
          <Label className="text-screen-md">{caption}</Label>
        </div>
      )}
    </Panel>
  );
}

/** Maps a session's voter set onto card faces for the hidden phase. */
export function hiddenBallots(
  players: PublicPlayer[],
  eligibleVoterIds: string[],
  votedPlayerIds: string[],
): Ballot[] {
  const voted = new Set(votedPlayerIds);
  return eligibleVoterIds
    .map((id) => players.find((p) => p.id === id))
    .filter((p): p is PublicPlayer => p !== undefined)
    .map((player) => ({ player, face: voted.has(player.id) ? 'DOWN' : 'PENDING' }));
}

/** Maps a revealed session's recorded votes onto card faces. */
export function revealedBallots(
  players: PublicPlayer[],
  votes: Array<{ playerId: string; choice: VoteChoice; name: string }>,
): Ballot[] {
  return votes.map((vote) => {
    const player = players.find((p) => p.id === vote.playerId);
    return {
      // A voter who has since been kicked still appears, from the record.
      player:
        player ??
        ({
          id: vote.playerId,
          name: vote.name,
          initials: vote.name.slice(0, 2).toUpperCase(),
          colorIndex: 0,
          hasAvatar: false,
          avatarVersion: 0,
          alive: true,
          isAdmin: false,
        } satisfies PublicPlayer),
      face: vote.choice,
    };
  });
}
